/**
 * Dropdown Cell Editor - Single select dropdown with search
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Check, ChevronDown, Search, Loader2 } from "lucide-react";
import CellEditorWrapper from "./CellEditorWrapper";
import { DropdownCellEditorProps, CellOption } from "./types";

const CellEditorDropdown: React.FC<DropdownCellEditorProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  isVisible,
  cellRect,
  cellWidth,
  options,
  isLoading = false,
  searchable = true,
  placeholder = "Select an option...",
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Get currently selected option
  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === value);
  }, [options, value]);

  // Focus search input when visible
  useEffect(() => {
    if (isVisible && searchInputRef.current && searchable) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isVisible, searchable]);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current) {
      const highlightedEl = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  // Handle option selection
  const handleSelect = useCallback(
    (option: CellOption) => {
      onChange(option.value);
      // Auto-save on selection
      setTimeout(() => onSave(), 50);
    },
    [onChange, onSave]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            Math.min(prev + 1, filteredOptions.length - 1)
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filteredOptions[highlightedIndex]) {
            handleSelect(filteredOptions[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onCancel();
          break;
      }
    },
    [filteredOptions, highlightedIndex, handleSelect, onCancel]
  );

  return (
    <CellEditorWrapper
      isVisible={isVisible}
      cellRect={cellRect}
      cellWidth={cellWidth}
      onSave={onSave}
      onCancel={onCancel}
      minHeight={44}
      maxHeight={320}
    >
      <div className="flex flex-col h-full" onKeyDown={handleKeyDown}>
        {/* Search input */}
        {searchable && (
          <div className="flex items-center px-2 py-2 border-b border-border">
            <Search className="h-4 w-4 text-muted-foreground mr-2 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="flex-1 text-sm border-none outline-none bg-transparent text-foreground"
            />
          </div>
        )}

        {/* Options list */}
        <div ref={listRef} className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm">Loading options...</span>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <span className="text-sm">No options found</span>
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = option.value === value;
              const isHighlighted = index === highlightedIndex;

              return (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`
                    flex items-center px-3 py-2 cursor-pointer transition-colors
                    ${isHighlighted ? "bg-primary/10" : ""}
                    ${isSelected ? "bg-primary/20" : ""}
                    hover:bg-primary/10
                  `}
                >
                  {/* Avatar/Icon if provided */}
                  {option.avatar && (
                    <img
                      src={option.avatar}
                      alt=""
                      className="h-6 w-6 rounded-full mr-2 flex-shrink-0"
                    />
                  )}
                  {option.icon && !option.avatar && (
                    <span className="mr-2 flex-shrink-0">{option.icon}</span>
                  )}

                  {/* Color indicator if provided */}
                  {option.color && (
                    <span
                      className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: option.color }}
                    />
                  )}

                  {/* Label */}
                  <span className="flex-1 text-sm text-foreground truncate">
                    {option.label}
                  </span>

                  {/* Check mark for selected */}
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Current selection indicator */}
        {selectedOption && (
          <div className="px-3 py-2 border-t border-border bg-muted">
            <span className="text-xs text-muted-foreground">Selected: </span>
            <span className="text-xs font-medium text-foreground">
              {selectedOption.label}
            </span>
          </div>
        )}
      </div>
    </CellEditorWrapper>
  );
};

export default CellEditorDropdown;

